import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const RED    = '#DC2626'

// Pathao required columns
const PATHAO_COLUMNS = [
  { key: 'recipient_name',      label: 'Recipient Name',        pathao: 'Recipient Name',       required: true,  source: 'customer_name'    },
  { key: 'recipient_phone',     label: 'Recipient Phone',       pathao: 'Recipient Phone',      required: true,  source: 'customer_phone'   },
  { key: 'recipient_address',   label: 'Recipient Address',     pathao: 'Recipient Address',    required: true,  source: 'address_full'     },
  { key: 'cod_amount',          label: 'COD Amount',            pathao: 'COD Amount',           required: true,  source: 'total_amount'     },
  { key: 'weight',              label: 'Item Weight (kg)',      pathao: 'Item Weight',          required: true,  source: 'weight_kg'        },
  { key: 'item_quantity',       label: 'Item Quantity',         pathao: 'Item Quantity',        required: true,  source: 'total_quantity'   },
  { key: 'item_description',    label: 'Item Description',      pathao: 'Item Description',     required: true,  source: 'products_summary' },
  { key: 'merchant_order_id',   label: 'PageConnect Order ID',  pathao: 'Merchant Order ID',    required: true,  source: 'order_code'       },
  { key: 'special_instruction', label: 'Special Instruction',   pathao: 'Special Instruction',  required: false, source: 'notes'            },
]

const EXTRA_COLUMNS = [
  { key: 'order_date',     label: 'Order Date',     source: 'order_date'     },
  { key: 'payment_status', label: 'Payment Status', source: 'payment_status' },
  { key: 'order_status',   label: 'Order Status',   source: 'order_status'   },
  { key: 'district',       label: 'District',       source: 'district'       },
  { key: 'thana',          label: 'Thana/Upazilla', source: 'thana'          },
  { key: 'unit_price',     label: 'Unit Price',     source: 'unit_price'     },
  { key: 'delivery_charge',label: 'Delivery Charge',source: 'delivery_charge'},
  { key: 'product_code',   label: 'Product Code',   source: 'product_code'   },
  { key: 'courier',        label: 'Courier',        source: 'courier'        },
  { key: 'taken_by',       label: 'Taken By',       source: 'taken_by'       },
]

const STATUS_LABELS = {
  received: 'Received', pending: 'Pending', confirmed: 'Confirmed',
  processing: 'Processing', with_courier: 'With Courier',
  shipped: 'Shipped', closed: 'Closed', return: 'Return',
}

function getOrderValue(order, source, defaultWeight, orderItemsMap) {
  const c    = order.customers || {}
  const items = orderItemsMap[order.id] || []

  const addressParts = [
    c.house && c.house !== 'N/A' ? c.house : null,
    c.road && c.road !== 'N/A' ? c.road : null,
    c.moholla && c.moholla !== 'N/A' ? c.moholla : null,
    c.neighbourhood && c.neighbourhood !== 'N/A' ? c.neighbourhood : null,
    c.metro_thana && c.metro_thana !== 'N/A' ? c.metro_thana : null,
    c.union_name && c.union_name !== 'N/A' ? c.union_name : null,
    c.thana && c.thana !== 'N/A' ? c.thana : null,
    c.district && c.district !== 'N/A' ? c.district : null,
  ].filter(Boolean).join(', ')

  // Calculate total weight from order_items
  const calcWeight = () => {
    if (items.length > 0) {
      let total = 0
      for (const item of items) {
        const w = parseFloat(item.weight_kg || 0)
        const q = Number(item.quantity || 1)
        if (w > 0) {
          total += w * q
        } else {
          total += parseFloat(defaultWeight || 0.5) * q
        }
      }
      return total > 0 ? total.toFixed(3) : defaultWeight || '0.5'
    }
    // Fallback to single product
    const w = parseFloat(order.weight_kg || 0)
    const q = Number(order.quantity || 1)
    return w > 0 ? (w * q).toFixed(3) : defaultWeight || '0.5'
  }

  // Products summary for item_description
  const calcProductsSummary = () => {
    if (items.length > 0) {
      return items.map(item => {
        const name = item.product_name || ''
        const variant = item.variant_name ? ' (' + item.variant_name + ')' : ''
        const qty = item.quantity > 1 ? ' x' + item.quantity : ''
        return name + variant + qty
      }).join(', ')
    }
    return (order.product_name || '') + (order.variant_name ? ' (' + order.variant_name + ')' : '')
  }

  // Total quantity across all items
  const calcTotalQty = () => {
    if (items.length > 0) {
      return items.reduce((s, i) => s + Number(i.quantity || 1), 0)
    }
    return order.quantity || 1
  }

  switch (source) {
    case 'customer_name':    return c.name || ''
    case 'customer_phone':   return c.phone || ''
    case 'address_full':     return addressParts || ''
    case 'total_amount':     return Number(order.total_amount || 0).toFixed(0)
    case 'weight_kg':        return calcWeight()
    case 'total_quantity':   return String(calcTotalQty())
    case 'products_summary': return calcProductsSummary()
    case 'order_code':       return order.order_code || ''
    case 'notes':            return order.notes || ''
    case 'order_date':       return new Date(order.created_at).toLocaleDateString('en-BD')
    case 'payment_status':   return order.payment_status || ''
    case 'order_status':     return STATUS_LABELS[order.status] || order.status || ''
    case 'district':         return c.district && c.district !== 'N/A' ? c.district : ''
    case 'thana':            return c.thana && c.thana !== 'N/A' ? c.thana : ''
    case 'unit_price':       return Number(order.unit_price || 0).toFixed(0)
    case 'delivery_charge':  return Number(order.delivery_charge || 0).toFixed(0)
    case 'product_code':     return order.product_code || ''
    case 'courier':          return order.courier || ''
    case 'taken_by':         return order.taken_by || ''
    default:                 return ''
  }
}

function Icon({ path, size = 14, color = MUTED, strokeWidth = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  x:       'M6 18L18 6M6 6l12 12',
  drag:    'M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01',
  plus:    'M12 4v16m8-8H4',
  trash:   'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  download:'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3',
  lock:    'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  info:    'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  edit:    'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
}

export default function CourierExportModal({ orders, onClose, sym }) {
  const [columns, setColumns]             = useState(PATHAO_COLUMNS.map(c => ({ ...c, enabled: true, customLabel: c.pathao })))
  const [filterType, setFilterType]       = useState('all')
  const [filterStatus, setFilterStatus]   = useState([])
  const [dateFrom, setDateFrom]           = useState('')
  const [dateTo, setDateTo]               = useState('')
  const [defaultWeight, setDefaultWeight] = useState('0.5')
  const [showAddCol, setShowAddCol]       = useState(false)
  const [dragIdx, setDragIdx]             = useState(null)
  const [preview, setPreview]             = useState([])
  const [orderItemsMap, setOrderItemsMap] = useState({})  // { order_id: [items] }
  const [loadingItems, setLoadingItems]   = useState(false)
  const [exportCount, setExportCount]     = useState(null)
  // Editable weight per order
  const [weightOverrides, setWeightOverrides] = useState({})

  // Fetch order_items for all visible orders
  useEffect(() => {
    if (orders.length === 0) return
    const fetchItems = async () => {
      setLoadingItems(true)
      const orderIds = orders.map(o => o.id)
      const { data } = await supabase
        .from('order_items')
        .select('order_id, product_id, product_name, variant_name, quantity, unit_price, weight_kg, line_subtotal, product_code')
        .in('order_id', orderIds)
      const map = {}
      for (const item of data || []) {
        if (!map[item.order_id]) map[item.order_id] = []
        map[item.order_id].push(item)
      }
      setOrderItemsMap(map)
      setLoadingItems(false)
    }
    fetchItems()
  }, [orders])

  // Build preview whenever settings change
  useEffect(() => {
    if (loadingItems) return
    const filtered = getFilteredOrders()
    setExportCount(filtered.length)
    const enabledCols = columns.filter(c => c.enabled)
    const rows = filtered.slice(0, 3).map(o => {
      const effectiveWeight = weightOverrides[o.id] || null
      return enabledCols.map(col => {
        if (col.source === 'weight_kg' && effectiveWeight !== null) return effectiveWeight
        return getOrderValue(o, col.source, defaultWeight, orderItemsMap)
      })
    })
    setPreview(rows)
  }, [columns, filterType, filterStatus, dateFrom, dateTo, defaultWeight, orderItemsMap, loadingItems, weightOverrides]) // eslint-disable-line

  const getFilteredOrders = () => {
    let result = [...orders]
    if (filterType === 'status' && filterStatus.length > 0) {
      result = result.filter(o => filterStatus.includes(o.status))
    }
    if (filterType === 'date') {
      if (dateFrom) result = result.filter(o => new Date(o.created_at) >= new Date(dateFrom))
      if (dateTo)   result = result.filter(o => new Date(o.created_at) <= new Date(dateTo + 'T23:59:59'))
    }
    return result
  }

  const toggleStatus  = (s) => setFilterStatus(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])
  const removeColumn  = (key) => setColumns(prev => prev.filter(c => c.key !== key))
  const updateLabel   = (key, val) => setColumns(prev => prev.map(c => c.key === key ? { ...c, customLabel: val } : c))
  const addExtraColumn = (col) => { if (columns.find(c => c.key === col.key)) return; setColumns(prev => [...prev, { ...col, enabled: true, customLabel: col.label, required: false, pathao: col.label }]); setShowAddCol(false) }

  const handleDragStart = (idx) => setDragIdx(idx)
  const handleDragOver  = (e, idx) => {
    e.preventDefault()
    if (dragIdx === null || dragIdx === idx) return
    const newCols = [...columns]
    const [moved] = newCols.splice(dragIdx, 1)
    newCols.splice(idx, 0, moved)
    setColumns(newCols)
    setDragIdx(idx)
  }
  const handleDragEnd = () => setDragIdx(null)

  const handleExport = () => {
    const filtered    = getFilteredOrders()
    const enabledCols = columns.filter(c => c.enabled)
    const headers     = enabledCols.map(c => c.customLabel)
    const rows = filtered.map(o => {
      const effectiveWeight = weightOverrides[o.id] || null
      return enabledCols.map(col => {
        if (col.source === 'weight_kg' && effectiveWeight !== null) return effectiveWeight
        return getOrderValue(o, col.source, defaultWeight, orderItemsMap)
      })
    })
    const csv = [headers, ...rows]
      .map(r => r.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(','))
      .join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = 'pathao-bulk-' + new Date().toISOString().slice(0, 10) + '.csv'
    a.click()
    URL.revokeObjectURL(url)
    onClose()
  }

  const filteredCount   = exportCount !== null ? exportCount : orders.length
  const enabledCols     = columns.filter(c => c.enabled)
  const availableExtra  = EXTRA_COLUMNS.filter(ec => !columns.find(c => c.key === ec.key))
  const filtered3       = getFilteredOrders().slice(0, 3)

  const inp = {
    border: '1px solid ' + BORDER, borderRadius: '8px', padding: '7px 10px',
    fontSize: '12px', outline: 'none', background: WHITE, color: TEXT,
    fontFamily: 'Space Grotesk, sans-serif', width: '100%', boxSizing: 'border-box',
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: WHITE, borderRadius: '16px', width: '100%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', fontFamily: 'Space Grotesk, sans-serif', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT }}>Bulk Courier Export</div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>Pathao format · weight from products · configure columns · download CSV</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <Icon path={IC.x} color={MUTED} size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* Step 1 — Filter */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
              Step 1 — Which orders to export?
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {[{ k: 'all', l: 'All orders' }, { k: 'status', l: 'By status' }, { k: 'date', l: 'By date' }].map(opt => (
                <button key={opt.k} onClick={() => setFilterType(opt.k)}
                  style={{ padding: '7px 16px', borderRadius: '8px', border: '1.5px solid', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: filterType === opt.k ? BLUE : WHITE, color: filterType === opt.k ? WHITE : TEXT, borderColor: filterType === opt.k ? BLUE : BORDER }}>
                  {opt.l}
                </button>
              ))}
            </div>

            {filterType === 'status' && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <button key={key} onClick={() => toggleStatus(key)}
                    style={{ padding: '5px 12px', borderRadius: '20px', border: '1px solid', fontSize: '12px', fontWeight: 500, cursor: 'pointer', background: filterStatus.includes(key) ? TEXT : WHITE, color: filterStatus.includes(key) ? WHITE : MUTED, borderColor: filterStatus.includes(key) ? TEXT : BORDER }}>
                    {label} ({orders.filter(o => o.status === key).length})
                  </button>
                ))}
              </div>
            )}

            {filterType === 'date' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px', fontWeight: 600 }}>From</div>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inp} />
                </div>
                <div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '5px', fontWeight: 600 }}>To</div>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inp} />
                </div>
              </div>
            )}

            <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '8px', background: filteredCount > 0 ? '#F0FDF4' : '#FEF2F2', border: '1px solid ' + (filteredCount > 0 ? '#BBF7D0' : '#FECACA') }}>
              <span style={{ fontSize: '12.5px', fontWeight: 700, color: filteredCount > 0 ? GREEN : RED }}>
                {filteredCount} order{filteredCount !== 1 ? 's' : ''} will be exported
              </span>
              {loadingItems && <span style={{ fontSize: '11.5px', color: MUTED, marginLeft: '8px' }}>· loading weights...</span>}
            </div>
          </div>

          {/* Step 2 — Default weight */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
              Step 2 — Default weight (fallback)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="number" value={defaultWeight} onChange={e => setDefaultWeight(e.target.value)}
                step="0.1" min="0.1" style={{ ...inp, width: '100px' }} />
              <span style={{ fontSize: '12.5px', color: MUTED }}>kg — used only when a product has no weight set</span>
            </div>
            <div style={{ fontSize: '11px', color: MUTED, marginTop: '6px' }}>
              💡 Weight is calculated automatically from each product's weight × quantity. Set this fallback for products without a weight.
            </div>
          </div>

          {/* Step 3 — Per-order weight override */}
          {filtered3.length > 0 && !loadingItems && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                Step 3 — Review & edit weights
              </div>
              <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '10px' }}>
                Weights auto-calculated from products. Edit any order's weight before exporting.
              </div>
              <div style={{ border: '1px solid ' + BORDER, borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0', background: BG, padding: '8px 12px', borderBottom: '1px solid ' + BORDER }}>
                  <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>Order</div>
                  <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>Products</div>
                  <div style={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>Weight (kg)</div>
                </div>
                {getFilteredOrders().map(o => {
                  const autoWeight = getOrderValue(o, 'weight_kg', defaultWeight, orderItemsMap)
                  const items = orderItemsMap[o.id] || []
                  const productSummary = items.length > 0
                    ? items.map(i => i.product_name + (i.variant_name ? ' (' + i.variant_name + ')' : '') + ' ×' + i.quantity).join(', ')
                    : (o.product_name || '—')
                  return (
                    <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', padding: '8px 12px', borderBottom: '1px solid ' + BORDER, alignItems: 'center' }}>
                      <div style={{ fontSize: '11.5px', fontWeight: 700, color: BLUE, fontFamily: 'JetBrains Mono, monospace' }}>{o.order_code}</div>
                      <div style={{ fontSize: '11px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={productSummary}>{productSummary}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input type="number" step="0.001" min="0.1"
                          value={weightOverrides[o.id] !== undefined ? weightOverrides[o.id] : autoWeight}
                          onChange={e => setWeightOverrides(prev => ({ ...prev, [o.id]: e.target.value }))}
                          style={{ ...inp, width: '80px', padding: '5px 8px', textAlign: 'center' }} />
                        {weightOverrides[o.id] !== undefined && (
                          <button onClick={() => setWeightOverrides(prev => { const n = {...prev}; delete n[o.id]; return n })}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: MUTED, fontSize: '12px' }} title="Reset to auto">↺</button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 4 — Columns */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
              Step 4 — Configure columns
            </div>
            <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '10px' }}>
              Drag to reorder · Edit header name · Delete optional columns
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
              {columns.map((col, idx) => (
                <div key={col.key} draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '9px', border: '1px solid ' + (dragIdx === idx ? BLUE : BORDER), background: dragIdx === idx ? '#E7F3FF' : WHITE, cursor: 'grab', transition: 'border-color 0.15s' }}>
                  <div style={{ cursor: 'grab', flexShrink: 0 }}>
                    <Icon path={IC.drag} color={MUTED} size={14} strokeWidth={2} />
                  </div>
                  <div style={{ width: '20px', flexShrink: 0, fontSize: '10.5px', fontWeight: 700, color: MUTED, textAlign: 'center' }}>{idx + 1}</div>
                  <input value={col.customLabel} onChange={e => updateLabel(col.key, e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12.5px', fontWeight: 600, color: TEXT, background: 'transparent', fontFamily: 'Space Grotesk, sans-serif', cursor: 'text' }} />
                  <div style={{ fontSize: '10.5px', color: BLUE, background: '#E7F3FF', padding: '2px 8px', borderRadius: '20px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {col.source}
                  </div>
                  {col.required ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                      <Icon path={IC.lock} color={MUTED} size={11} strokeWidth={2} />
                      <span style={{ fontSize: '10px', color: MUTED }}>required</span>
                    </div>
                  ) : (
                    <button onClick={() => removeColumn(col.key)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <Icon path={IC.trash} color={RED} size={13} strokeWidth={2} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {availableExtra.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowAddCol(o => !o)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: '1.5px dashed ' + BORDER, background: WHITE, fontSize: '12px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                  <Icon path={IC.plus} color={MUTED} size={12} strokeWidth={2.5} />
                  Add extra column
                </button>
                {showAddCol && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', padding: '6px', zIndex: 10, minWidth: '200px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                    {availableExtra.map(col => (
                      <button key={col.key} onClick={() => addExtraColumn(col)}
                        style={{ display: 'flex', width: '100%', padding: '8px 10px', borderRadius: '7px', border: 'none', background: WHITE, fontSize: '12.5px', fontWeight: 500, color: TEXT, cursor: 'pointer', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = BG}
                        onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                        {col.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
                Preview — first 3 rows
              </div>
              <div style={{ overflowX: 'auto', borderRadius: '9px', border: '1px solid ' + BORDER }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace' }}>
                  <thead>
                    <tr style={{ background: BG }}>
                      {enabledCols.map(col => (
                        <th key={col.key} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: MUTED, whiteSpace: 'nowrap', borderBottom: '1px solid ' + BORDER }}>
                          {col.customLabel}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, ri) => (
                      <tr key={ri} style={{ borderBottom: ri < preview.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                        {row.map((cell, ci) => (
                          <td key={ci} style={{ padding: '7px 10px', color: TEXT, whiteSpace: 'nowrap', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {cell || <span style={{ color: '#D1D5DB' }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '11px', color: MUTED, marginTop: '5px' }}>
                Showing {Math.min(3, filteredCount)} of {filteredCount} orders
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid ' + BORDER, display: 'flex', gap: '10px', flexShrink: 0, background: WHITE }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleExport} disabled={filteredCount === 0 || loadingItems}
            style={{ flex: 2, padding: '11px', borderRadius: '10px', border: 'none', background: (filteredCount === 0 || loadingItems) ? MUTED : BLUE, fontSize: '13px', fontWeight: 700, color: WHITE, cursor: (filteredCount === 0 || loadingItems) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
            <Icon path={IC.download} color={WHITE} size={14} strokeWidth={2.5} />
            {loadingItems ? 'Loading weights...' : 'Download CSV — ' + filteredCount + ' order' + (filteredCount !== 1 ? 's' : '')}
          </button>
        </div>
      </div>
    </div>
  )
}